import { ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { TabValue } from '@fluentui/react-components';
import {
  NavOverview,
  TResolvedItemsMap,
  CreateVersionDialog,
  ItemsNavDrawer,
  useItemScreenStore,
  EItemScreenMode,
  SCREEN_MODE_TO_PARAM_MAP,
  AUTO_EDIT_QUERY_PARAM,
  AUTO_EDIT_QUERY_VALUE,
  FileIcon,
  useRename,
  HistoryTab,
  useNavDrawerWithSidebar,
} from '@modules/items';
import { TPermissions, AccessDialog, TAccessDialogState } from '@modules/access';
import { ToastContext, LayoutContext } from '@shared/contexts';
import { SignInContext } from '@modules/users';
import {
  TCompleteInlineBlockResult,
  TInlineBlock,
  TInlineBlockDraft,
  TInlineBlockVersion,
  TSaveInlineBlockResult,
} from '../types';
import { useDocumentTitle, useSearchParams } from '@shared/hooks';
import { useScreenWidth } from '@shared/hooks/useScreenWidth.hook';
import { useEditInlineBlockDraft, useSaveInlineBlock } from '../api';
import { SingleFieldDialog, TSingleFieldDialogState } from '@shared/components';
import { AllComments } from '@modules/comments';
import classNames from 'classnames';
import { AVAILABLE_SCREEN_MODES } from '../constants';
import { useView, NavBlockInsights, PORTRAIT_PAPER_WIDTH_PX, SIDEBAR_WIDTH_PX } from '@modules/documents';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import useInlineBlockNavActions from '../hooks/useInlineBlockNavActions.hook';
import InlineBlockHeader from './InlineBlockHeader';
import InlineBlockEditor from './InlineBlockEditor';
import InlineBlockConfigDialog from './dialogs/InlineBlockConfigDialog';
import { useDebouncedCallback } from 'use-debounce';
import { normalizeUrl, yupUrlTransform, isInsecureUrl } from '@shared/utils';

type TProps = {
  inlineBlock: TInlineBlock;
  permissions: TPermissions;
  resolvedItems: TResolvedItemsMap;
};

// yup schema for the form
const schema = yup
  .object({
    value: yup.string().required('Value is required'),
    url: yup
      .string()
      .nullable()
      .notRequired()
      .transform(yupUrlTransform)
      .test('https-only', 'Only HTTPS links are allowed – HTTP is not secure', (v) => !v || !isInsecureUrl(v))
      .url('Must be a valid URL'),
  })
  .required();

export type TFormData = yup.InferType<typeof schema>;

// slightly (2x) smaller than for documents, since inline-blocks are usually a lot smaller
const AUTOSAVE_DELAY = 1.5 * 1000;
const AUTOSAVE_MAX = 10 * 1000;

// comments are currently not available for the customers
const COMMENTS_ALLOWED_EMAILS = [
  'jan.schweiger@confideck.com',
  'lukas.poterpin@confideck.com',
  'ruslan.kotliarenko@confideck.com',
  'victor.ramirez@confideck.com',
];

const InlineBlockContainer = ({ inlineBlock, permissions, resolvedItems }: TProps) => {
  useDocumentTitle(inlineBlock.name);

  // --- Misc states
  const [isEdit, setIsEdit] = useState(false);
  const [selectedTab, setSelectedTab] = useState<TabValue>('home');
  // Controlled by explicit user actions (Add URL / Delete URL / Stop editing).
  // NOT reset by autosave or version-effect while in edit mode.
  const [showUrlField, setShowUrlField] = useState(false);

  // --- Dialog states
  const [simpleDialogState, setSimpleDialogState] = useState<TSingleFieldDialogState<'input'> | undefined>(undefined);
  const [accessDialogState, setAccessDialogState] = useState<TAccessDialogState>();
  const [isCreateVersionDialogOpen, setIsCreateVersionDialogOpen] = useState<boolean>(false);
  const [isOpenInlineBlockConfig, setIsOpenInlineBlockConfig] = useState(false);

  // --- Contexts and hooks
  const { show } = useContext(ToastContext);
  const { user } = useContext(SignInContext);
  const { layoutState } = useContext(LayoutContext);
  const { setLayoutState } = useContext(LayoutContext);
  const { screenMode, setScreenMode } = useItemScreenStore();
  const { searchParams, changeMany } = useSearchParams();

  // --- Form
  const formData = useForm<TFormData>({
    resolver: yupResolver(schema),
    defaultValues: { value: '', url: '' },
  });
  const value = formData.watch('value');
  const url = formData.watch('url');
  const isSaved = !formData.formState.isDirty;

  // --- Autosave
  const debouncedSave = useDebouncedCallback(
    async () => {
      // Only save if form is valid
      const isValid = await formData.trigger();
      if (isValid) {
        save.mutate(
          { id: inlineBlock._id, value, url: normalizeUrl(url) ?? null },
          { onSuccess: () => {} } // silent save, no UI feedback
        );
      }
    },
    AUTOSAVE_DELAY,
    { maxWait: AUTOSAVE_MAX }
  );
  // debounce whenever the form value or url changes (only in edit mode with unsaved changes)
  useEffect(() => {
    if (isEdit && !isSaved) debouncedSave();
  }, [value, url]);
  // used on focus-out to immediately save the form value
  const flushAutosave = async () => {
    const isValid = await formData.trigger();
    if (isValid) debouncedSave.flush();
  };

  // --- Version control
  const viewData = useView(inlineBlock, permissions, isEdit, false); // retry is not needed for inline-blocks
  const version = viewData.version as TInlineBlockVersion | TInlineBlockDraft | undefined;
  useEffect(() => {
    if (version) {
      formData.reset({ value: version.value, url: version.url ?? '' });
      if (!isEdit) setShowUrlField(!!version.url);
    }
  }, [version]);
  const viewParam = searchParams.get('view');
  const autoEdit = searchParams.get(AUTO_EDIT_QUERY_PARAM) === AUTO_EDIT_QUERY_VALUE;
  useEffect(() => {
    if (viewParam !== 'edit') setIsEdit(false); // reset edit mode if the view param is not 'edit'
  }, [viewParam]);

  // --- API hooks
  const save = useSaveInlineBlock();
  const edit = useEditInlineBlockDraft();

  const enterEditMode = useCallback(() => setIsEdit(true), []);
  const leaveEditMode = useCallback(() => {
    changeMany({ view: undefined, [AUTO_EDIT_QUERY_PARAM]: undefined });
    setIsEdit(false);
  }, []);

  // --- API handlers
  const defaultOnSuccess = (data: TSaveInlineBlockResult) => {
    leaveEditMode();
    // set saved value in case it was changed (e.g. trimmed) on the backend
    formData.reset({ value: data.draft.value, url: data.draft.url ?? '' });
    setShowUrlField(!!data.draft.url); // hide empty URL field when leaving edit mode
    show('success', 'Draft saved.');
  };
  const handleSave = async (opts: { customOnSuccess?: () => void } = {}) => {
    if (isEdit && isSaved) {
      if (opts.customOnSuccess) {
        opts.customOnSuccess();
      } else {
        leaveEditMode();
        setShowUrlField(!!url?.trim());
      }
      return;
    }
    // Validate form before saving
    const isValid = await formData.trigger();
    if (!isValid) return; // Don't save if form has validation errors

    save.mutate(
      { id: inlineBlock._id, value, url: normalizeUrl(url) ?? null },
      { onSuccess: opts.customOnSuccess ?? defaultOnSuccess }
    );
  };
  const handleComplete = () => {
    // try to save the draft first if we are editing
    if (isEdit)
      handleSave({
        customOnSuccess: () => {
          setIsCreateVersionDialogOpen(true);
        },
      });
    else setIsCreateVersionDialogOpen(true);
  };
  const handlePostComplete = () => {
    leaveEditMode(); // reset edit mode only after a successful completion (good UX)
  };
  const handleEditDraft = () => {
    changeMany({ view: 'edit', [AUTO_EDIT_QUERY_PARAM]: undefined });
    // create a draft if it doesn't exist
    if (!inlineBlock.draft) edit.mutate(inlineBlock._id, { onSuccess: enterEditMode });
    else enterEditMode();
  };
  useEffect(() => {
    if (!autoEdit || isEdit) return;
    handleEditDraft();
  }, [autoEdit, isEdit, inlineBlock._id]);

  // --- Rename handler
  const rename = useRename();
  const onRename = () => {
    const dialogState: TSingleFieldDialogState<'input'> = {
      isOpen: true,
      defaultText: inlineBlock.name,
      title: 'Rename',
      label: 'Name',
      buttonLabel: 'Rename',
      onSubmit: (data) => {
        rename.mutate(
          { id: inlineBlock._id, itemType: inlineBlock.type, name: data.text },
          { onSuccess: () => setSimpleDialogState({ ...dialogState, isOpen: false }) }
        );
      },
    };
    setSimpleDialogState(dialogState);
  };
  useEffect(() => {
    setLayoutState({
      _id: inlineBlock._id,
      name: inlineBlock.name,
      icon: (
        <FileIcon
          path={inlineBlock.resolvedPath.map((pathItem) => pathItem._id)}
          type={inlineBlock.type}
          isBlock
          isFrozen={inlineBlock.isFrozen}
          isTemplate={false}
          size="small"
        />
      ),
      resolvedPath: inlineBlock.resolvedPath,
      onRename,
    });
  }, [inlineBlock._id, inlineBlock.name, inlineBlock.resolvedPath]);

  // --- Helpers
  const handleShowAccess = () => {
    setAccessDialogState({
      isOpen: true,
      id: inlineBlock._id,
      nodeType: 'INLINE_BLOCK',
      name: inlineBlock.name,
    });
  };

  // --- URL handlers
  const handleAddUrl = () => {
    setShowUrlField(true);
  };
  const handleDeleteUrl = () => {
    debouncedSave.cancel(); // prevent stale debounced save from firing after the immediate one
    setShowUrlField(false); // hide immediately so the user doesn't see the field clear first
    formData.setValue('url', '', { shouldDirty: true });
    // Send null (not undefined) so JSON serialization preserves the key;
    // the backend uses $unset to remove the field from the document.
    save.mutate(
      { id: inlineBlock._id, value, url: null },
      {
        onSuccess: (data) => {
          formData.reset({ value: data.draft.value, url: '' });
          show('info', 'URL deleted.');
        },
      }
    );
  };

  // --- Right-side screen mode
  const screenModeParam = searchParams.get('screenMode');
  useEffect(() => {
    /**
     * If there's a ?screenMode=someId in the URL and the screen mode is not set:
     * - find the `EItemScreenMode` value based on the URL param
     * - set the screen mode (if available for inline-blocks)
     */
    if (screenModeParam && !screenMode) {
      // Find the `EItemScreenMode` value based on the URL param
      const newScreenMode = Object.keys(SCREEN_MODE_TO_PARAM_MAP).find(
        (key) => SCREEN_MODE_TO_PARAM_MAP[key as EItemScreenMode] === screenModeParam
      ) as EItemScreenMode | undefined;

      if (!newScreenMode) console.error(`Unknown screen mode param: ${screenModeParam}`);
      else if (!AVAILABLE_SCREEN_MODES.includes(newScreenMode))
        console.error(`Screen mode not available: ${newScreenMode}`);
      else setScreenMode(newScreenMode);
    }
  }, []);

  // --- Left-side nav actions and tabs
  const actions = useInlineBlockNavActions({
    version,
    inlineBlock,
    permissions,
    isEdit,
    handleSave,
    handleEditDraft,
    handleComplete,
    handleShowAccess,
    handleOpenSettings: () => setIsOpenInlineBlockConfig(true),
    layoutState,
  });
  const tabs: Record<string, ReactNode> = {
    Overview: (
      <NavOverview
        item={inlineBlock}
        actions={actions}
        itemVersion={version && { type: inlineBlock.type, item: inlineBlock, version }}
        permissions={permissions}
      />
    ),
    Insights: <NavBlockInsights block={inlineBlock} version={version} resolvedItems={resolvedItems} />,
  };

  const hasCommentsSidebar =
    COMMENTS_ALLOWED_EMAILS.includes(user.mail) && Boolean(version) && screenMode === EItemScreenMode.COMMENTS;
  const { isOpenDrawer, setIsOpenDrawer } = useNavDrawerWithSidebar({
    hasSidebar: hasCommentsSidebar,
    contentWidthPx: PORTRAIT_PAPER_WIDTH_PX,
    sidebarWidthPx: SIDEBAR_WIDTH_PX,
  });

  // --- Responsive design
  const screenWidth = useScreenWidth();
  const isWideScreen = screenWidth > 1550;
  let maxWidth = 794; // content
  maxWidth += 2 * 40; // spacing between
  if (screenMode) maxWidth += 300;
  if (isWideScreen || !isOpenDrawer)
    maxWidth = Math.max(maxWidth, 1024); // max-w-5xl
  else maxWidth = Math.max(maxWidth, 1152); // max-w-6xl
  let paddingRight = 0;
  if (isOpenDrawer && isWideScreen) {
    if (!screenMode) paddingRight = 315;
    else paddingRight = 165;
  }

  return (
    <div className="flex flex-1 min-h-0" style={{ paddingRight }}>
      {/* Left-side navigation drawer */}
      <ItemsNavDrawer
        isOpen={isOpenDrawer}
        setIsOpen={setIsOpenDrawer}
        tabs={tabs}
        resolvedPath={inlineBlock.resolvedPath}
      />

      {/* Dialogs */}
      <SingleFieldDialog state={simpleDialogState} setState={setSimpleDialogState} />
      <AccessDialog state={accessDialogState} setState={setAccessDialogState} />
      <CreateVersionDialog
        isOpen={isCreateVersionDialogOpen}
        setIsOpen={setIsCreateVersionDialogOpen}
        item={inlineBlock}
        resolvedItems={resolvedItems}
        disconnect={handlePostComplete}
      />
      <InlineBlockConfigDialog
        isOpen={isOpenInlineBlockConfig}
        setIsOpen={setIsOpenInlineBlockConfig}
        inlineBlock={inlineBlock}
        version={version}
        permissions={permissions}
        urlConfig={{ showUrlField, isEdit, handleAddUrl, handleDeleteUrl }}
      />

      {/* Main content */}
      <div
        className={classNames('relative grow h-full min-h-0 px-4', { 'mx-auto pl-3': isWideScreen || !isOpenDrawer })}
        style={{ maxWidth: `${maxWidth}px` }}
      >
        <div className="h-full min-h-0 flex flex-col">
          <InlineBlockHeader
            inlineBlock={inlineBlock}
            isEdit={isEdit}
            isSaved={isSaved}
            isError={save.isError || edit.isError}
            handleEditDraft={handleEditDraft}
            handleSave={handleSave}
            handleComplete={handleComplete}
            version={version}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            permissions={permissions}
            handleShowAccess={handleShowAccess}
          />
          <div className="w-full flex-1 min-h-0 relative overflow-y-auto">
            {selectedTab === 'history' ? (
              <HistoryTab item={inlineBlock} permissions={permissions} setSelectedTab={setSelectedTab} />
            ) : (
              <div className="flex flex-1 h-full mx-auto pl-1 w-full">
                <InlineBlockEditor
                  formData={formData}
                  inlineBlock={inlineBlock}
                  isEdit={isEdit}
                  flushAutosave={flushAutosave}
                  showUrlField={showUrlField}
                />

                {/* --- comment are currently not available for the customers --- */}
                {hasCommentsSidebar && version && (
                  <AllComments
                    item={inlineBlock}
                    permissions={permissions}
                    version={version}
                    resolvedItems={resolvedItems}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InlineBlockContainer;
